import axios from 'axios'
import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  ChevronRight,
  KeyRound,
  MessageSquare,
  Moon,
  Palette,
  Sun,
  Target,
  Trash2,
  User,
} from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { settingsApi } from '@/api/settings'
import { articlesApi } from '@/api/articles'
import type { ReviewMode } from '@/api/review'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { cn } from '@/components/ui/cn'
import { useAuthStore } from '@/store/authStore'
import { clearReviewSession } from '@/lib/reviewSessionStorage'
import { useSettingsStore } from '@/store/settingsStore'
import { LogoutButton } from '@/components/auth/LogoutButton'
import { NativeLanguageChangeDialog } from '@/components/settings/NativeLanguageChangeDialog'
import { FeedbackForm } from '@/components/feedback/FeedbackForm'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { GUEST_LOCALES, isGuestLocale, setGuestLocale, type GuestLocale } from '@/lib/guestLocale'
import { AiCreditsIndicator } from '@/components/ai/AiCreditsIndicator'
import { useAiCredits } from '@/hooks/useAiCredits'
import {
  CEFR_LEVEL_OPTIONS,
  NATIVE_LANGUAGE_OPTIONS,
  nativeLanguageSelectOptions,
} from '@/types/settings'
import type { UpdateSettingsRequest, UserSettings } from '@/types/settings'
import type { CefrLevel } from '@/types/common'
import { clearLearningDemoCompleted } from '@/lib/learningDemoStorage'

// ─── Schemas ─────────────────────────────────────────────────────────────────

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

const deleteSchema = z.object({
  password: z.string().min(1, 'Password is required'),
  confirmation: z
    .string()
    .refine((v) => v === 'DELETE', { message: 'Type DELETE exactly to confirm' }),
})

type PasswordFormValues = z.infer<typeof passwordSchema>
type DeleteFormValues = z.infer<typeof deleteSchema>

// ─── Tab definitions ─────────────────────────────────────────────────────────

const TAB_IDS = [
  'preferences',
  'appearance',
  'goals',
  'notifications',
  'feedback',
  'security',
  'account',
] as const

type TabId = (typeof TAB_IDS)[number]

const TAB_ICONS = {
  preferences: User,
  appearance: Palette,
  goals: Target,
  notifications: Bell,
  feedback: MessageSquare,
  security: KeyRound,
  account: Trash2,
} as const

function useSettingsTabs() {
  const { settingsChrome: s } = useMinCopy()
  const labels: Record<TabId, string> = {
    preferences: s.tabPreferences,
    appearance: s.tabAppearance,
    goals: s.tabGoals,
    notifications: s.tabNotifications,
    feedback: s.tabFeedback,
    security: s.tabSecurity,
    account: s.tabAccount,
  }
  return TAB_IDS.map((id) => ({ id, label: labels[id], icon: TAB_ICONS[id] }))
}

function parseSettingsTab(value: string | null): TabId | null {
  if (!value) return null
  return (TAB_IDS as readonly string[]).includes(value) ? (value as TabId) : null
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-base font-semibold">{children}</h2>
}

function SectionDescription({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>
}

function SavedBadge({ show }: { show: boolean }) {
  const { actions } = useMinCopy()
  return (
    <span
      className={cn(
        'text-sm text-emerald-600 transition-opacity duration-300',
        show ? 'opacity-100' : 'opacity-0'
      )}
    >
      {actions.saved}
    </span>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const navigate = useNavigate()
  const { pages, settingsChrome } = useMinCopy()
  const TABS = useSettingsTabs()
  const [searchParams] = useSearchParams()
  const tabFromUrl = parseSettingsTab(searchParams.get('tab'))
  const [activeTab, setActiveTab] = useState<TabId>(tabFromUrl ?? 'preferences')
  const [mobileInSection, setMobileInSection] = useState(() => tabFromUrl !== null)
  const queryClient = useQueryClient()
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const { theme, setTheme, setSettings, clearSettings } = useSettingsStore()

  useEffect(() => {
    const next = parseSettingsTab(searchParams.get('tab'))
    if (next) {
      setActiveTab(next)
      setMobileInSection(true)
      return
    }
    if (!searchParams.get('tab')) {
      setActiveTab('preferences')
      setMobileInSection(false)
    }
  }, [searchParams])

  const { data: settings, isLoading, isError } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.getSettings,
    staleTime: Infinity,
  })

  const updateMutation = useMutation({
    mutationFn: settingsApi.updateSettings,
    onSuccess: (updated, patch) => {
      setSettings(updated)
      queryClient.setQueryData(['settings'], updated)
      if (patch.nativeLanguage && patch.nativeLanguage !== settings?.nativeLanguage) {
        clearReviewSession()
        queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
        queryClient.invalidateQueries({ queryKey: ['vocabularyPhrases'] })
        queryClient.invalidateQueries({ queryKey: ['knownPhrases'] })
        queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] })
        queryClient.invalidateQueries({ queryKey: ['reviewDueCards'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['learningPlan'] })
        queryClient.invalidateQueries({ queryKey: ['collections'] })
      }
    },
  })

  const handleImmediate = (patch: UpdateSettingsRequest) => {
    updateMutation.mutate(patch)
  }

  const activeTabMeta = TABS.find((tab) => tab.id === activeTab) ?? TABS[0]

  const openMobileSection = (tabId: TabId) => {
    setActiveTab(tabId)
    setMobileInSection(true)
  }

  const renderActiveSection = () => (
    <>
      {activeTab === 'preferences' && (
        <PreferencesSection settings={settings!} onSave={handleImmediate} />
      )}
      {activeTab === 'appearance' && (
        <AppearanceSection
          theme={theme}
          uiLanguage={settings!.uiLanguage}
          onThemeChange={(t) => {
            setTheme(t)
            handleImmediate({ theme: t })
          }}
          onUiLanguageChange={(locale) => {
            handleImmediate({ uiLanguage: locale })
          }}
        />
      )}
      {activeTab === 'goals' && (
        <GoalsSection settings={settings!} onSave={handleImmediate} />
      )}
      {activeTab === 'notifications' && (
        <NotificationsSection settings={settings!} onSave={handleImmediate} />
      )}
      {activeTab === 'feedback' && <FeedbackSection />}
      {activeTab === 'security' && <SecuritySection />}
      {activeTab === 'account' && (
        <AccountSection
          onDeleted={() => {
            clearAuth()
            clearSettings()
            navigate('/', { replace: true })
          }}
        />
      )}
    </>
  )

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isError || !settings) {
    return (
      <EmptyState
        title={settingsChrome.loadFailed}
        description={settingsChrome.loadFailed}
      />
    )
  }

  /** Prefer previous in-app screen; fall back to Today when history is empty (direct open / refresh). */
  const leaveSettings = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1)
      return
    }
    navigate('/dashboard')
  }

  return (
    <div className="space-y-6">
      {/* Desktop: leave-settings link (sidebar still primary; helps after deep-link entry) */}
      <button
        type="button"
        onClick={leaveSettings}
        className="mb-1 hidden items-center gap-1 text-sm text-muted-foreground hover:text-foreground lg:flex"
        data-testid="settings-go-back"
      >
        <ArrowLeft className="h-4 w-4" />
        {settingsChrome.goBack}
      </button>

      {/* Mobile header slot — fixed height to prevent jump between list and drill-in */}
      <div className="flex h-14 items-center lg:hidden">
        {mobileInSection ? (
          <>
            <button
              type="button"
              onClick={() => setMobileInSection(false)}
              className="mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={settingsChrome.backToSettings}
              data-testid="settings-mobile-back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">{pages.settings}</p>
              <h1 className="truncate text-lg font-semibold">{activeTabMeta.label}</h1>
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={leaveSettings}
              className="mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={settingsChrome.goBack}
              data-testid="settings-mobile-go-back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold">{pages.settings}</h1>
              <p className="text-xs text-muted-foreground">{pages.settingsSubtitle}</p>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        {/* Desktop sidebar */}
        <nav
          className="hidden w-48 shrink-0 flex-col gap-1 lg:flex"
          data-testid="settings-desktop-sidebar"
        >
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                activeTab === id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </button>
          ))}
        </nav>

        {/* Mobile section list */}
        {!mobileInSection ? (
          <nav className="grid gap-2 lg:hidden" data-testid="settings-mobile-menu">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => openMobileSection(id)}
                data-testid={`settings-mobile-nav-${id}`}
                className="flex min-h-12 w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-muted/50"
              >
                <Icon className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                <span className="flex-1 text-sm font-medium text-foreground">{label}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              </button>
            ))}
          </nav>
        ) : null}

        {/* Tab content — desktop always; mobile when drilled in */}
        <div
          className={cn('min-w-0 flex-1', !mobileInSection && 'hidden lg:block')}
          data-testid="settings-section-content"
        >
          {renderActiveSection()}
        </div>
      </div>
    </div>
  )
}

// ─── Preferences section ──────────────────────────────────────────────────────

function PreferencesSection({
  settings,
  onSave,
}: {
  settings: Pick<
    UserSettings,
    | 'nativeLanguage'
    | 'timezone'
    | 'targetEnglishLevel'
    | 'sessionDurationMinutes'
    | 'includeQuickChallenge'
    | 'preferredTags'
    | 'defaultReviewMode'
  >
  onSave: (patch: UpdateSettingsRequest) => void
}) {
  const navigate = useNavigate()
  const userId = useAuthStore((state) => state.user?.id)
  const min = useMinCopy()
  const demo = min.learningDemo
  const settingsCopy = min.settings
  const chrome = min.settingsChrome
  const [saved, setSaved] = useState(false)
  const [nativeLanguage, setNativeLanguage] = useState(settings.nativeLanguage)
  const [pendingNativeLanguage, setPendingNativeLanguage] = useState<string | null>(null)
  const [targetEnglishLevel, setTargetEnglishLevel] = useState(settings.targetEnglishLevel)
  const [sessionDurationMinutes, setSessionDurationMinutes] = useState(settings.sessionDurationMinutes)
  const [includeQuickChallenge, setIncludeQuickChallenge] = useState(settings.includeQuickChallenge)
  const [preferredTags, setPreferredTags] = useState<string[]>(settings.preferredTags ?? [])
  const [defaultReviewMode, setDefaultReviewMode] = useState(settings.defaultReviewMode ?? 'CLASSIC')

  const { data: articleTags } = useQuery({
    queryKey: ['article-tags'],
    queryFn: () => articlesApi.getTags().then((r) => r.data),
  })

  const handleSave = () => {
    onSave({
      nativeLanguage,
      targetEnglishLevel: targetEnglishLevel as CefrLevel,
      sessionDurationMinutes,
      includeQuickChallenge,
      preferredTags,
      defaultReviewMode,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const togglePreferredTag = (tag: string) => {
    setPreferredTags((current) =>
      current.includes(tag) ? current.filter((value) => value !== tag) : [...current, tag],
    )
  }

  const nativeLanguageLabel = (code: string) =>
    NATIVE_LANGUAGE_OPTIONS.find((option) => option.code === code)?.label ?? code

  const handleNativeLanguageChange = (nextLanguage: string) => {
    if (nextLanguage === nativeLanguage) return
    if (nextLanguage === settings.nativeLanguage) {
      setNativeLanguage(nextLanguage)
      return
    }
    setPendingNativeLanguage(nextLanguage)
  }

  const confirmNativeLanguageChange = () => {
    if (pendingNativeLanguage) {
      setNativeLanguage(pendingNativeLanguage)
    }
    setPendingNativeLanguage(null)
  }

  const cancelNativeLanguageChange = () => {
    setPendingNativeLanguage(null)
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <SectionTitle>{chrome.preferencesTitle}</SectionTitle>
          <SectionDescription>{chrome.preferencesDescription}</SectionDescription>
        </div>

        <AiCreditsIndicator />

        <div className="rounded-xl border border-border/60 bg-muted/10 p-4">
          <p className="text-sm font-medium">{demo.replay}</p>
          <p className="mt-1 text-xs text-muted-foreground">{demo.replayDescription}</p>
          <Button
            type="button"
            className="mt-3 h-9 w-auto bg-secondary px-4 text-secondary-foreground hover:bg-secondary/80"
            onClick={() => {
              if (userId) {
                clearLearningDemoCompleted(userId)
              }
              navigate('/learning-demo?replay=1')
            }}
            data-testid="learning-demo-replay"
          >
            {demo.replay}
          </Button>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="targetEnglishLevel">{chrome.targetLevel}</Label>
            <select
              id="targetEnglishLevel"
              value={targetEnglishLevel}
              onChange={(e) => setTargetEnglishLevel(e.target.value)}
              className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {CEFR_LEVEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              {chrome.targetLevelHint}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sessionDuration">
              {formatMessage(chrome.sessionLengthLabel, { minutes: sessionDurationMinutes })}
            </Label>
            <input
              id="sessionDuration"
              type="range"
              min={5}
              max={15}
              step={1}
              value={sessionDurationMinutes}
              onChange={(e) => setSessionDurationMinutes(Number(e.target.value))}
              className="w-full accent-primary"
            />
            <p className="text-xs text-muted-foreground">
              {chrome.sessionLength}
            </p>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div className="space-y-1">
              <Label htmlFor="includeQuickChallenge">{chrome.includeQuickChallenge}</Label>
              <p className="text-xs text-muted-foreground">{chrome.includeQuickChallengeHint}</p>
            </div>
            <input
              id="includeQuickChallenge"
              type="checkbox"
              checked={includeQuickChallenge}
              onChange={(e) => setIncludeQuickChallenge(e.target.checked)}
              className="mt-1 h-4 w-4 accent-primary"
            />
          </div>

          {articleTags && articleTags.length > 0 ? (
            <div className="space-y-2">
              <Label>{chrome.preferredTopics}</Label>
              <div className="flex flex-wrap gap-2">
                {articleTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => togglePreferredTag(tag)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors',
                      preferredTags.includes(tag)
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/40',
                    )}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{chrome.preferredTopicsHint}</p>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="defaultReviewMode">{chrome.defaultReviewMode}</Label>
            <select
              id="defaultReviewMode"
              value={defaultReviewMode}
              onChange={(e) => setDefaultReviewMode(e.target.value as ReviewMode)}
              className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="settings-default-review-mode"
            >
              <option value="CLASSIC">Classic — word first</option>
              <option value="REVERSE">Reverse — translation first</option>
              <option value="FILL_BLANK">Fill in the blank</option>
              <option value="MULTIPLE_CHOICE">Multiple choice</option>
            </select>
            <p className="text-xs text-muted-foreground">{chrome.defaultReviewModeHint}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="nativeLanguage">{settingsCopy.learningLanguageLabel}</Label>
            <select
              id="nativeLanguage"
              value={nativeLanguage}
              onChange={(e) => handleNativeLanguageChange(e.target.value)}
              className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="settings-native-language"
            >
              {nativeLanguageSelectOptions(nativeLanguage).map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              {settingsCopy.learningLanguageHint}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">{chrome.timezone}</Label>
            <Input id="timezone" value={settings.timezone} readOnly disabled className="bg-muted/50" />
            <p className="text-xs text-muted-foreground">
              {chrome.timezoneHint}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button className="w-auto px-6" onClick={handleSave}>
            {chrome.savePreferences}
          </Button>
          <SavedBadge show={saved} />
        </div>
      </div>

      {pendingNativeLanguage ? (
        <NativeLanguageChangeDialog
          languageLabel={nativeLanguageLabel(pendingNativeLanguage)}
          onConfirm={confirmNativeLanguageChange}
          onCancel={cancelNativeLanguageChange}
        />
      ) : null}
    </Card>
  )
}

// ─── Appearance section ───────────────────────────────────────────────────────

function AppearanceSection({
  theme,
  uiLanguage,
  onThemeChange,
  onUiLanguageChange,
}: {
  theme: 'light' | 'dark'
  uiLanguage: string
  onThemeChange: (theme: 'light' | 'dark') => void
  onUiLanguageChange: (locale: GuestLocale) => void
}) {
  const t = useMinCopy().settings
  const selectedUi = isGuestLocale(uiLanguage) ? uiLanguage : 'en'

  const handleUiChange = (next: GuestLocale) => {
    setGuestLocale(next)
    onUiLanguageChange(next)
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <SectionTitle>{t.appearanceTitle}</SectionTitle>
          <SectionDescription>{t.appearanceDescription}</SectionDescription>
        </div>

        <div className="space-y-2">
          <Label htmlFor="settings-ui-language">{t.uiLanguageLabel}</Label>
          <select
            id="settings-ui-language"
            value={selectedUi}
            onChange={(e) => handleUiChange(e.target.value as GuestLocale)}
            className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="settings-ui-language"
          >
            {GUEST_LOCALES.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.nativeLabel}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">{t.uiLanguageHint}</p>
        </div>

        <div className="space-y-2">
          <Label>{t.themeLabel}</Label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onThemeChange('light')}
              className={cn(
                'flex flex-1 items-center justify-center gap-2 rounded-lg border-2 p-4 text-sm font-medium transition-colors',
                theme === 'light'
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/50'
              )}
            >
              <Sun className="h-4 w-4" />
              {t.themeLight}
            </button>
            <button
              type="button"
              onClick={() => onThemeChange('dark')}
              className={cn(
                'flex flex-1 items-center justify-center gap-2 rounded-lg border-2 p-4 text-sm font-medium transition-colors',
                theme === 'dark'
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/50'
              )}
            >
              <Moon className="h-4 w-4" />
              {t.themeDark}
            </button>
          </div>
        </div>
      </div>
    </Card>
  )
}

// ─── Goals section ────────────────────────────────────────────────────────────

type GoalRange = { min: number; max: number }

function DailyGoalField({
  id,
  label,
  value,
  range,
  disabled,
  hint,
  onChange,
}: {
  id: string
  label: string
  value: number
  range: GoalRange
  disabled?: boolean
  hint?: string
  onChange: (value: number) => void
}) {
  const clamp = (next: number) => Math.min(range.max, Math.max(range.min, next))

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={id}>{label}</Label>
        <span className="text-sm font-semibold tabular-nums text-foreground">{value}</span>
      </div>
      <input
        id={`${id}-slider`}
        type="range"
        min={range.min}
        max={range.max}
        step={1}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(clamp(Number(e.target.value)))}
        className="w-full accent-primary disabled:cursor-not-allowed disabled:opacity-50"
        data-testid={`${id}-slider`}
        aria-valuemin={range.min}
        aria-valuemax={range.max}
        aria-valuenow={value}
      />
      <Input
        id={id}
        type="number"
        min={range.min}
        max={range.max}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const parsed = Number(e.target.value)
          if (!Number.isFinite(parsed)) return
          onChange(clamp(parsed))
        }}
        className="w-24"
        data-testid={id}
      />
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

function GoalsSection({
  settings,
  onSave,
}: {
  settings: {
    dailyGoalNewWords: number
    dailyGoalReviews: number
    dailyGoalArticles: number
    dailyGoalPractice: number
  }
  onSave: (patch: UpdateSettingsRequest) => void
}) {
  const { settingsChrome: chrome } = useMinCopy()
  const { usage } = useAiCredits()
  const isPracticeLocked = usage?.tier === 'FREE'
  const [saved, setSaved] = useState(false)
  const [newWords, setNewWords] = useState(settings.dailyGoalNewWords)
  const [reviews, setReviews] = useState(settings.dailyGoalReviews)
  const [articles, setArticles] = useState(settings.dailyGoalArticles)
  const [practice, setPractice] = useState(settings.dailyGoalPractice)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setNewWords(settings.dailyGoalNewWords)
    setReviews(settings.dailyGoalReviews)
    setArticles(settings.dailyGoalArticles)
    setPractice(settings.dailyGoalPractice)
  }, [
    settings.dailyGoalNewWords,
    settings.dailyGoalReviews,
    settings.dailyGoalArticles,
    settings.dailyGoalPractice,
  ])

  const handleSave = () => {
    if (newWords < 1 || newWords > 50) {
      setError('New words must be between 1 and 50')
      return
    }
    if (reviews < 5 || reviews > 200) {
      setError('Reviews must be between 5 and 200')
      return
    }
    if (articles < 0 || articles > 5) {
      setError('Articles must be between 0 and 5')
      return
    }
    if (practice < 0 || practice > 3) {
      setError('AI practice must be between 0 and 3')
      return
    }

    setError(null)
    onSave({
      dailyGoalNewWords: newWords,
      dailyGoalReviews: reviews,
      dailyGoalArticles: articles,
      dailyGoalPractice: practice,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <SectionTitle>{chrome.goalsTitle}</SectionTitle>
          <SectionDescription>
            {chrome.goalsDescription}
          </SectionDescription>
        </div>

        <div className="space-y-6">
          <DailyGoalField
            id="goal-new-words"
            label={`${chrome.goalsNewWords} (1–50)`}
            value={newWords}
            range={{ min: 1, max: 50 }}
            onChange={setNewWords}
          />

          <DailyGoalField
            id="goal-reviews"
            label={`${chrome.goalsReviews} (5–200)`}
            value={reviews}
            range={{ min: 5, max: 200 }}
            onChange={setReviews}
          />

          <DailyGoalField
            id="goal-articles"
            label={`${chrome.goalsArticles} (0–5)`}
            value={articles}
            range={{ min: 0, max: 5 }}
            onChange={setArticles}
          />

          <DailyGoalField
            id="goal-practice"
            label={`${chrome.goalsPractice} (0–3)`}
            value={isPracticeLocked ? 0 : practice}
            range={{ min: 0, max: 3 }}
            disabled={isPracticeLocked}
            hint={isPracticeLocked ? chrome.goalsPracticeProHint : undefined}
            onChange={setPractice}
          />
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="flex items-center gap-3">
          <Button className="w-auto px-6" onClick={handleSave}>
            {chrome.saveGoals}
          </Button>
          <SavedBadge show={saved} />
        </div>
      </div>
    </Card>
  )
}

// ─── Notifications section ────────────────────────────────────────────────────

function NotificationsSection({
  settings: _settings,
}: {
  settings: { notificationsEnabled: boolean }
  onSave: (patch: UpdateSettingsRequest) => void
}) {
  const { settingsChrome: chrome } = useMinCopy()
  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <SectionTitle>{chrome.notificationsTitle}</SectionTitle>
          <SectionDescription>{chrome.notificationsDescription}</SectionDescription>
        </div>

        <div className="flex items-center justify-between gap-4 rounded-lg border border-dashed border-border bg-muted/30 p-4">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">Enable notifications</p>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {chrome.notificationsComingSoon}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {chrome.notificationsComingSoonHint}
            </p>
          </div>
          <button
            type="button"
            disabled
            role="switch"
            aria-checked={false}
            aria-disabled="true"
            className="relative inline-flex h-6 w-11 shrink-0 cursor-not-allowed rounded-full border-2 border-transparent bg-muted opacity-60"
            data-testid="notifications-toggle-disabled"
          >
            <span className="pointer-events-none inline-block h-5 w-5 translate-x-0 transform rounded-full bg-white shadow-lg" />
          </button>
        </div>
      </div>
    </Card>
  )
}

// ─── Feedback section ─────────────────────────────────────────────────────────

function FeedbackSection() {
  const { feedback } = useMinCopy()
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <SectionTitle>{feedback.settingsTitle}</SectionTitle>
        <SectionDescription>{feedback.settingsHint}</SectionDescription>
        <div className="mt-4">
          <FeedbackForm pagePath="/settings" requireEmailWhenGuest={false} />
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Looking for how Lexio works?{' '}
          <a href="/faq" className="text-primary hover:underline">
            Read the FAQ
          </a>
          .
        </p>
      </Card>
    </div>
  )
}

// ─── Security section ─────────────────────────────────────────────────────────

function SecuritySection() {
  const { settingsChrome: chrome } = useMinCopy()
  const [saved, setSaved] = useState(false)

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <SectionTitle>{chrome.securitySignOutTitle}</SectionTitle>
            <SectionDescription>{chrome.securitySignOutHint}</SectionDescription>
          </div>
          <LogoutButton variant="outline" className="w-full sm:w-auto" />
        </div>
      </Card>

      <ChangePasswordCard saved={saved} setSaved={setSaved} />
    </div>
  )
}

function ChangePasswordCard({
  saved,
  setSaved,
}: {
  saved: boolean
  setSaved: (value: boolean) => void
}) {
  const { settingsChrome: chrome } = useMinCopy()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
  })

  const onSubmit = async (data: PasswordFormValues) => {
    try {
      await settingsApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      })
      reset()
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('currentPassword', { message: 'Current password is incorrect' })
      } else {
        setError('root', { message: 'Something went wrong. Please try again.' })
      }
    }
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <SectionTitle>{chrome.changePasswordTitle}</SectionTitle>
          <SectionDescription>{chrome.changePasswordDescription}</SectionDescription>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current password</Label>
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              hasError={Boolean(errors.currentPassword)}
              {...register('currentPassword')}
            />
            {errors.currentPassword ? (
              <p className="text-sm text-destructive">{errors.currentPassword.message}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">New password</Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              hasError={Boolean(errors.newPassword)}
              {...register('newPassword')}
            />
            {errors.newPassword ? (
              <p className="text-sm text-destructive">{errors.newPassword.message}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              hasError={Boolean(errors.confirmPassword)}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword ? (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            ) : null}
          </div>

          {errors.root?.message ? (
            <p className="text-sm text-destructive">{errors.root.message}</p>
          ) : null}

          <div className="flex items-center gap-3">
            <Button type="submit" className="w-auto px-6" loading={isSubmitting}>
              Change password
            </Button>
            <SavedBadge show={saved} />
          </div>
        </form>
      </div>
    </Card>
  )
}

// ─── Account section ──────────────────────────────────────────────────────────

function AccountSection({ onDeleted }: { onDeleted: () => void }) {
  const { settingsChrome: chrome } = useMinCopy()
  const [showModal, setShowModal] = useState(false)

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <SectionTitle>{chrome.accountTitle}</SectionTitle>
          <SectionDescription>{chrome.accountDescription}</SectionDescription>
        </div>

        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div className="space-y-2">
              <p className="text-sm font-semibold text-destructive">Danger Zone</p>
              <p className="text-sm text-muted-foreground">
                Deleting your account is permanent. All your vocabulary, progress, and data will be
                removed and cannot be recovered.
              </p>
              <Button
                className="w-auto bg-destructive px-4 text-destructive-foreground hover:bg-destructive/90"
                onClick={() => setShowModal(true)}
              >
                Delete my account
              </Button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <DeleteAccountModal onClose={() => setShowModal(false)} onDeleted={onDeleted} />
      )}
    </Card>
  )
}

// ─── Delete confirmation modal ────────────────────────────────────────────────

function DeleteAccountModal({
  onClose,
  onDeleted,
}: {
  onClose: () => void
  onDeleted: () => void
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<DeleteFormValues>({
    resolver: zodResolver(deleteSchema),
  })

  const onSubmit = async (data: DeleteFormValues) => {
    try {
      await settingsApi.deleteAccount({ password: data.password, confirmation: data.confirmation })
      onDeleted()
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('password', { message: 'Password is incorrect' })
      } else {
        setError('root', { message: 'Something went wrong. Please try again.' })
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md space-y-5 rounded-xl bg-background p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <h2 className="text-base font-semibold">Confirm Account Deletion</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              This action cannot be undone. Enter your password and type <strong>DELETE</strong> to
              confirm.
            </p>
          </div>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <Label htmlFor="delPassword">Password</Label>
            <Input
              id="delPassword"
              type="password"
              autoComplete="current-password"
              hasError={Boolean(errors.password)}
              {...register('password')}
            />
            {errors.password ? (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmation">
              Type <span className="font-mono font-bold">DELETE</span> to confirm
            </Label>
            <Input
              id="confirmation"
              type="text"
              hasError={Boolean(errors.confirmation)}
              {...register('confirmation')}
            />
            {errors.confirmation ? (
              <p className="text-sm text-destructive">{errors.confirmation.message}</p>
            ) : null}
          </div>

          {errors.root?.message ? (
            <p className="text-sm text-destructive">{errors.root.message}</p>
          ) : null}

          <div className="flex gap-3">
            <Button
              type="button"
              className="flex-1 border border-border bg-background text-foreground shadow-none hover:bg-muted"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90"
              loading={isSubmitting}
            >
              Delete account
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
